import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CaseDetailWithRtw,
  CaseSummary,
  CompleteTouchpointRequest,
  TouchpointSummary,
} from '@besideu/shared/cases';
import { api, ApiError } from '@/lib/api.ts';
import { CaseNotFound } from '@/components/CaseNotFound.tsx';
import { CaseDetailView } from '@/components/CaseDetailView.tsx';
import { ManagerMaeveTouchpointPrep } from '@/components/ManagerMaeveTouchpointPrep.tsx';
import { ManagerMaeveFirstResponse } from '@/components/ManagerMaeveFirstResponse.tsx';
import { ManagerMaeveGuidance } from '@/components/ManagerMaeveGuidance.tsx';
import { ManagerRtwPlan } from '@/components/ManagerRtwPlan.tsx';
import { EmployeePreferencesPanel } from '@/components/EmployeePreferencesPanel.tsx';
import { SignificantDatesPanel } from '@/components/SignificantDatesPanel.tsx';
import {
  TabList,
  TabPanel,
  focusTab,
  useTabInSearchParam,
  type TabDef,
} from '@/components/ui/Tabs.tsx';
import { StatusPill, ToneFor, humaniseStatus } from '@/components/ui/StatusPill.tsx';
import { useLiveAnnouncer } from '@/lib/use-live-announcer.ts';
import { useSeedGuidanceDraft } from '@/lib/use-manager-guidance-chat.ts';

// Seeded (NOT auto-sent) into the case-detail guidance thread by the
// Significant dates panel's "What should I do about this?" button.
//
// Phrased as the manager's own question, and deliberately phrased around the
// limit rather than against it: the manager is not told what the date is
// (§11.2 visibility matrix — Manager is `limited` on `significantDates`) and
// neither is Maeve in Manager mode, so "without asking what it is" is the
// actual constraint both are working under, not a euphemism. It lands on
// Sub-mode B, whose system instruction already carries the same coarse signal
// this panel renders (`renderCaseBlock`).
//
// It is seeded into the composer rather than sent, so the manager can reword
// it — this is a CGC-audited endpoint, and nothing should reach it that the
// manager did not press Ask on.
const SIGNIFICANT_DATE_QUESTION =
  'A personally significant date is coming up for this employee. I have not been told what it is. How can I support them around it without asking them what the date is?';

const TAB_IDS = ['overview', 'plan', 'maeve'] as const;
type TabId = (typeof TAB_IDS)[number];
const TABS: Record<TabId, string> = {
  overview: 'Overview',
  plan: 'Support plan',
  maeve: 'Ask Maeve',
};

// PO Spec Phase 4 §1 (2026-06-21) used to bounce a phone User-Agent from here
// to a separate phone-shaped route (`/manager/mobile/cases/:id`) so the RTW
// plan form — the priority-1 mobile module — was a single tap away. There is
// no second route any more: `ManagerShell` renders this page inside the
// bottom-nav app shell below `md`, so the phone layout is a breakpoint, not a
// redirect, and an email deep-link straight to `/manager/cases/:id` lands
// correctly on any device without a UA sniff.
export function ManagerCaseDetail() {
  const { id = '' } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useTabInSearchParam('tab', TAB_IDS, 'overview');

  const detail = useQuery({
    queryKey: ['cases', id],
    queryFn: () => api.get<CaseDetailWithRtw>(`/api/cases/${id}`),
    enabled: id !== '',
  });

  // Slice 9 — manual mark-first-response-sent. Idempotent server-side; the UI
  // hides the button once the response is set, so double-fire isn't reachable.
  // This is the M2 fallback path for managers who don't draft through Maeve —
  // managers who DO draft use the M3 send endpoint inside
  // `ManagerMaeveFirstResponse`, which invalidates the same case query and
  // unmounts the manual button on the next render.
  const markSent = useMutation({
    mutationFn: () => api.post<{ case: CaseSummary }>(`/api/cases/${id}/mark-first-response-sent`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cases'] }),
  });

  // Support Framework spec §16 — manager completes a touch point with
  // structured detail. The server reconciles the case's HR dashboard flags;
  // invalidating the case query re-renders the timeline with the new status.
  const completeTouchpoint = useMutation({
    mutationFn: (vars: { touchpointId: string; completion: CompleteTouchpointRequest }) =>
      api.post<{ touchpoint: TouchpointSummary }>(
        `/api/cases/${id}/touchpoints/${vars.touchpointId}/done`,
        vars.completion,
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cases'] }),
  });

  // Badge count for the "Ask Maeve" tab, and a single shared sr-only
  // `aria-live` region for all three case-scoped Maeve surfaces below. Both
  // are reported UP from those components via callbacks rather than
  // computed here from a second, independent subscription to the guidance
  // thread — a live region (or a duplicate turns→questions calculation)
  // inside a `hidden` tab panel never reaches the user, so the parent hosts
  // one canonical copy of each, always mounted outside any `TabPanel`.
  // `useLiveAnnouncer` (not a plain `useState`) queues rather than
  // overwrites — all three Maeve surfaces are mounted simultaneously, and a
  // bare `setState(text)` from each would let two same-tick announcements
  // collapse into one commit, silently dropping whichever lost the race.
  const [guidanceQuestionCount, setGuidanceQuestionCount] = useState(0);
  const { message: liveAnnouncement, announce } = useLiveAnnouncer();

  // Significant dates → guidance hand-off (PO 2026-08-06). The panel below
  // shows the Manager the coarse "something's coming up" signal and, today,
  // dead-ends at "ask HR". This offers the other half — what to actually do
  // about it — as a seeded question on the case's existing guidance thread.
  const seedGuidanceDraft = useSeedGuidanceDraft(id, 'case-detail');

  // Timeline row → touch-point briefing hand-off (PO 2026-08-06). Parent state,
  // not a URL param: starting a briefing is a billed Vertex call and an audited
  // interaction, so it must not re-fire on refresh or on a shared link. See
  // `autoStartTouchpointId` in `ManagerMaeveTouchpointPrep`.
  const [prepTouchpointId, setPrepTouchpointId] = useState<string | null>(null);

  // Both hand-offs move the manager to a DIFFERENT tab panel, which leaves the
  // control they pressed inside a now-`hidden` subtree — focus silently falls
  // to `<body>`, stranding keyboard and screen-reader users mid-task. Focus has
  // to move in an effect rather than in the click handler: the target tab is
  // still hidden during the handler, and hidden elements are not focusable.
  const [pendingTabFocus, setPendingTabFocus] = useState<TabId | null>(null);
  useEffect(() => {
    if (!pendingTabFocus || activeTab !== pendingTabFocus) return;
    focusTab(pendingTabFocus);
    setPendingTabFocus(null);
  }, [pendingTabFocus, activeTab]);

  function goToMaeveTab(announcement: string) {
    setActiveTab('maeve');
    setPendingTabFocus('maeve');
    announce(announcement);
  }

  if (detail.isPending) return <p className="text-muted-foreground">Loading support pathway…</p>;
  if (detail.error) {
    if (detail.error instanceof ApiError && detail.error.status === 404) {
      return (
        <section className="space-y-6">
          <CaseNotFound
            backTo="/manager/team"
            reason="It may belong to another manager, or it may have been removed."
          />
        </section>
      );
    }
    return <p className="text-traffic-red">Could not load this support pathway.</p>;
  }

  const tabs: TabDef[] = TAB_IDS.map((id) =>
    id === 'maeve' && guidanceQuestionCount > 0
      ? {
          id,
          label: TABS[id],
          badge: (
            <StatusPill tone="info">
              {guidanceQuestionCount} {guidanceQuestionCount === 1 ? 'question' : 'questions'}
            </StatusPill>
          ),
        }
      : { id, label: TABS[id] },
  );

  const c = detail.data.case;

  return (
    <section className="space-y-6">
      {/* `/manager/team` — the list this link names. `/manager` has been the
          four action cards at every width since PO 2026-08-06. */}
      <Link
        to="/manager/team"
        className="text-sm text-muted-foreground underline-offset-4 hover:underline"
      >
        ← Back to support pathways
      </Link>

      {/* Persistent pathway identity — shown on the non-Overview tabs so
          switching tabs never loses sight of whose pathway this is. Omitted on
          Overview itself: `CaseDetailView`'s own header renders the same
          name + status pill one panel below, and showing both would just
          double it up on the tab a manager lands on by default. Deliberately
          just name + status, not a second copy of `CaseDetailView`'s details
          grid — that stays a single shared component with HR's case-detail
          page. */}
      {activeTab === 'overview' ? null : (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-medium text-foreground">{c.employee.name}</span>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <StatusPill tone={ToneFor(c.status)}>{humaniseStatus(c.status)}</StatusPill>
        </div>
      )}

      {/* `TabList.onChange` is `(id: string) => void` — it doesn't know about
          any particular page's tab-id union. The cast is safe here: every id
          it can call `onChange` with came from `tabs`, which this file built
          from `TAB_IDS`. */}
      <TabList
        tabs={tabs}
        active={activeTab}
        onChange={(id) => setActiveTab(id as TabId)}
        aria-label="Support pathway sections"
      />

      {/* Shared live region for the Maeve surfaces inside the tab panels
          below — see the state comment above. Always mounted outside any
          `TabPanel`, so an announcement still reaches the user even if the
          manager has switched away from the tab that triggered it. */}
      <div aria-live="polite" className="sr-only">
        {liveAnnouncement}
      </div>

      <TabPanel id="overview" active={activeTab === 'overview'}>
        <CaseDetailView
          data={detail.data}
          onMarkFirstResponseSent={() => markSent.mutate()}
          markSentPending={markSent.isPending}
          onCompleteTouchpoint={(touchpointId, completion) =>
            completeTouchpoint.mutate({ touchpointId, completion })
          }
          completingTouchpointId={
            completeTouchpoint.isPending
              ? (completeTouchpoint.variables?.touchpointId ?? null)
              : null
          }
          onPrepareTouchpoint={(touchpointId) => {
            setPrepTouchpointId(touchpointId);
            goToMaeveTab('Ask Maeve tab. Maeve is preparing a briefing for this touch point.');
          }}
        />
        <EmployeePreferencesPanel preferences={detail.data.employeePreferences} />
        <SignificantDatesPanel
          significantDates={detail.data.significantDates}
          onAskMaeve={() => {
            // The announcement differs by outcome, because "we put a question
            // in the box" and "we left the question you were already writing
            // alone" are different things to land on a tab you can't see yet.
            const result = seedGuidanceDraft(SIGNIFICANT_DATE_QUESTION);
            goToMaeveTab(
              result === 'seeded'
                ? 'Ask Maeve tab. A question about the upcoming significant date is ready in the composer — review it, then press Ask Maeve to send.'
                : 'Ask Maeve tab. The question already in the composer was kept, so the significant-date question was not added.',
            );
          }}
        />
      </TabPanel>

      {/* `key` here and on the three Maeve components below forces a full
          remount on caseId change — React Router keeps this page mounted
          across a `cases/:id` param-only navigation (e.g. browser back/
          forward between two cases), and without it each component's
          internal state would otherwise carry the previous case's content
          into the new case: here, the RTW date field + the private-notes/
          workload/support draft; below, drafted first-response variants,
          guidance history, and touch point prep history/pointers — all
          into the new case's CGC-audited surfaces. All three tab panels
          stay mounted (`TabPanel` hides via `hidden`, not conditional
          rendering) so switching tabs never triggers this remount either. */}
      {/* Each `key` is suffixed per-component — these are four sibling
          elements under the same parent, and React's sibling-key-uniqueness
          requirement applies across ALL of them, not just same-typed ones.
          Keying all four with the bare caseId (an earlier version of this
          fix did) produces four identical keys among siblings — React logs
          "Encountered two children with the same key" and reconciliation
          behavior in that state is explicitly documented as unsupported. */}
      <TabPanel id="plan" active={activeTab === 'plan'}>
        <ManagerRtwPlan
          key={`rtw-${detail.data.case.id}`}
          case={detail.data.case}
          rtwPlan={detail.data.rtwPlan}
        />
        <ManagerMaeveFirstResponse
          key={`first-response-${detail.data.case.id}`}
          caseId={detail.data.case.id}
          firstResponseSentAt={detail.data.case.firstResponseSentAt}
          contactChannel={detail.data.employeePreferences.visible.preferredContactChannel ?? null}
          contactPerson={detail.data.employeePreferences.visible.preferredContactPerson ?? null}
          onAnnounce={announce}
        />
      </TabPanel>

      <TabPanel id="maeve" active={activeTab === 'maeve'}>
        <ManagerMaeveGuidance
          key={`guidance-${detail.data.case.id}`}
          caseId={detail.data.case.id}
          threadId="case-detail"
          onAnnounce={announce}
          onQuestionCountChange={setGuidanceQuestionCount}
        />
        <ManagerMaeveTouchpointPrep
          key={`touchpoint-prep-${detail.data.case.id}`}
          caseId={detail.data.case.id}
          touchpoints={detail.data.touchpoints}
          autoStartTouchpointId={prepTouchpointId}
          onAnnounce={announce}
        />
      </TabPanel>
    </section>
  );
}
